import { Component, input, output } from '@angular/core';
import { CommonModule } from '@angular/common';

@Component({
  selector: 'app-confirmation-dialog',
  standalone: true,
  imports: [CommonModule],
  template: `
    @if (visible()) {
      <div
        class="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 bg-black/40 dark:bg-black/70 backdrop-blur-sm"
        (click)="handleCancel()"
      >
        <div
          class="bg-white dark:bg-stone-800 rounded-2xl sm:rounded-xl shadow-lg dark:shadow-fuchsia-900/20 max-w-xs sm:max-w-sm lg:max-w-md w-full p-4 sm:p-5 lg:p-6 relative border border-grounded/20 dark:border-stone-700/50 mx-auto"
          (click)="$event.stopPropagation()"
        >
          <!-- Header - Responsive -->
          <div class="flex justify-between items-start mb-3 sm:mb-4">
            <h2 class="text-lg sm:text-xl font-semibold text-gray-800 dark:text-stone-100 flex items-center gap-2 break-words flex-1 min-w-0 pr-2">
              @if (icon()) {
                <i [class]="'pi ' + icon() + ' text-pink-500 dark:text-fuchsia-400 text-base sm:text-lg flex-shrink-0'"></i>
              }
              <span class="break-words">{{ title() }}</span>
            </h2>
            <button
              (click)="handleCancel()"
              class="text-gray-400 dark:text-stone-400 hover:text-gray-600 dark:hover:text-stone-200 transition-colors duration-200 p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-stone-700/50 flex-shrink-0"
            >
              <i class="pi pi-times text-sm sm:text-base"></i>
            </button>
          </div>

          <!-- Message - Responsive -->
          <p class="text-sm sm:text-base text-gray-700 dark:text-stone-300 mb-4 sm:mb-6 leading-relaxed break-words">
            {{ message() }}
          </p>

          <!-- Actions - Responsive -->
          <div class="flex flex-col sm:flex-row justify-end gap-2 sm:gap-3 lg:gap-4">
            <button
              (click)="handleCancel()"
              class="w-full sm:w-auto px-4 py-2.5 sm:px-5 sm:py-3 lg:px-6 lg:py-3 bg-white/80 dark:bg-stone-700/80 text-rooted dark:text-stone-200 border border-grounded/30 dark:border-stone-600 rounded-xl font-medium hover:bg-sacred/30 dark:hover:bg-stone-600/50 hover:border-grounded/50 dark:hover:border-stone-500 transition-all duration-300 hover:scale-[1.02] active:scale-95 text-sm sm:text-base order-2 sm:order-1"
            >
              {{ cancelText() }}
            </button>
            <button
              (click)="handleConfirm()"
              class="w-full sm:w-auto px-4 py-2.5 sm:px-5 sm:py-3 lg:px-6 lg:py-3 bg-soulfire dark:bg-gradient-to-r dark:from-fuchsia-600 dark:to-fuchsia-700 hover:bg-bloom dark:hover:from-fuchsia-700 dark:hover:to-fuchsia-800 text-white font-semibold rounded-xl shadow-lg hover:shadow-soulfire/50 dark:shadow-fuchsia-500/25 dark:hover:shadow-fuchsia-500/40 hover:scale-[1.02] active:scale-95 transition-all duration-300 text-sm sm:text-base order-1 sm:order-2"
            >
              {{ confirmText() }}
            </button>
          </div>
        </div>
      </div>
    }
  `,
  styles: [
    `
      /* Ensure proper text wrapping */
      .break-words {
        word-wrap: break-word;
        word-break: break-word;
      }
      
      /* Proper flex behavior */
      .min-w-0 {
        min-width: 0;
      }

      .flex-shrink-0 {
        flex-shrink: 0;
      }

      /* Enhanced backdrop blur for better mobile UX */
      @supports (backdrop-filter: blur(8px)) {
        .backdrop-blur-sm {
          backdrop-filter: blur(8px);
        }
      }

      /* Responsive icon adjustments */
      @media (max-width: 640px) {
        .pi {
          font-size: 0.875rem;
        }
      }
      
      @media (min-width: 641px) and (max-width: 1024px) {
        .pi {
          font-size: 1rem;
        }
      }
      
      @media (min-width: 1025px) {
        .pi {
          font-size: 1.125rem;
        }
      }
    `,
  ],
})
export class ConfirmationDialogComponent {
  /**
   * Controls the visibility of the confirmation dialog.
   * This signal must be passed from the parent.
   */
  visible = input.required<boolean>();

  /**
   * The title shown in the dialog header.
   * Default: 'Confirm Action'
   */
  title = input('Confirm Action');

  /**
   * The body message shown in the dialog.
   * Default: 'Are you sure you want to continue?'
   */
  message = input('Are you sure you want to continue?');

  /**
   * Text for the confirm button.
   * Default: 'Yes'
   */
  confirmText = input('Yes');

  /**
   * Text for the cancel button.
   * Default: 'Cancel'
   */
  cancelText = input('Cancel');

  /**
   * PrimeNG icon class to be displayed beside the title.
   * Example: 'pi-question', 'pi-exclamation-triangle', 'pi-trash'
   * Default: 'pi-question'
   */
  icon = input<'pi-exclamation-triangle' | 'pi-question' | 'pi-trash' | string>('pi-question');

  /**
   * Emits when the user clicks the confirm button.
   * Use this to execute the primary action (e.g., delete, save).
   */
  confirmed = output<void>();

  /**
   * Emits when the user clicks cancel or outside the modal.
   * Use this to track dismissals or close-related logic.
   */
  cancelled = output<void>();

  /**
   * Emits the updated visibility state of the dialog.
   * Typically used to hide the dialog after confirm or cancel.
   */
  visibilityChanged = output<boolean>();

  /**
   * Called when the confirm button is clicked.
   * Emits `confirmed` and hides the dialog.
   */
  handleConfirm() {
    this.confirmed.emit();
    this.visibilityChanged.emit(false);
  }

  /**
   * Called when the cancel button or backdrop is clicked.
   * Emits `cancelled` and hides the dialog.
   */
  handleCancel() {
    this.cancelled.emit();
    this.visibilityChanged.emit(false);
  }
}